import { useState } from "react";
import { Tag } from "antd";

import { Frame } from "../../utils/apolloRenderer";

/**
 * 주행 상태 계기판 패널 — 3D 뷰 우측. 다크 테마.
 * 상단: 속도 hero + 기어/모드 배지(고정). 하단: 탭(주행 / 조향·경로 / 인지·판단 / 시스템).
 */

const GEAR_LABEL: { [k: number]: string } = { 0: "N", 1: "D", 2: "R", 3: "P", 4: "L", 5: "INVALID", 6: "NONE" };
const MODE_LABEL: { [k: number]: string } = { 0: "자율주행", 1: "미상", 2: "수동", 3: "비상정지", 4: "조향수동", 5: "속도수동", 6: "섀시오류" };
const MODE_COLOR: { [k: number]: string } = { 0: "#2ecc71", 1: "#8a99b0", 2: "#f39c12", 3: "#e74c3c", 4: "#f39c12", 5: "#f39c12", 6: "#e74c3c" };
const DECISION_LABEL: { [k: number]: string } = { 0: "무시", 1: "정지", 2: "넛지", 3: "양보", 4: "추월", 5: "추종", 6: "측면통과" };
const STOP_REASON_LABEL: { [k: number]: string } = { 1: "전방차량", 2: "목적지", 3: "보행자", 4: "장애물", 100: "신호등", 101: "정지표지", 102: "양보표지", 103: "클리어존", 104: "횡단보도", 105: "비상", 106: "준비안됨", 107: "길가정차" };
const SIGNAL_COLOR: { [k: string]: string } = { GREEN: "#2ecc71", RED: "#e74c3c", YELLOW: "#f1c40f", FLASHING_GREEN: "#2ecc71" };

const sectionTitle: React.CSSProperties = { fontSize: 15, color: "#8a99b0", margin: "16px 0 9px", borderBottom: "1px solid #232c3a", paddingBottom: 6, fontWeight: 600, letterSpacing: 0.5 };
const grid3: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 6 };
const grid2: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 6 };

const num = (v: number | undefined | null, d = 1, suffix = ""): string => (v != null && !isNaN(v) ? v.toFixed(d) + suffix : "-");
const rad2deg = (v?: number) => (v != null ? ((v * 180 / Math.PI + 360) % 360).toFixed(0) : "-");

function Badge({ label, value, color }: { label: string; value: React.ReactNode; color: string }) {
    return (
        <div style={{ flex: 1, background: "#141b26", border: `1px solid ${color}55`, borderRadius: 8, padding: "10px 4px", textAlign: "center" }}>
            <div style={{ fontSize: 14, color: "#8a99b0" }}>{label}</div>
            <div style={{ fontSize: 27, fontWeight: 800, color, lineHeight: 1.3 }}>{value}</div>
        </div>
    );
}

function Bar({ label, pct, valueText, color }: { label: string; pct: number; valueText: string; color: string }) {
    const w = Math.max(0, Math.min(100, pct));
    return (
        <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, marginBottom: 5 }}>
                <span style={{ color: "#8a99b0" }}>{label}</span>
                <span style={{ color: "#dbe4f2", fontWeight: 600 }}>{valueText}</span>
            </div>
            <div style={{ height: 10, background: "#1c2431", borderRadius: 5, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${w}%`, background: color, borderRadius: 5, transition: "width 0.15s ease" }} />
            </div>
        </div>
    );
}

function Chip({ label, value, unit, color }: { label: string; value: React.ReactNode; unit?: string; color?: string }) {
    return (
        <div style={{ background: "#141b26", border: "1px solid #232c3a", borderRadius: 8, padding: "11px 6px", textAlign: "center", minWidth: 0, overflow: "hidden" }}>
            <div style={{ fontSize: 14, color: "#8a99b0", marginBottom: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: color || "#dbe4f2", lineHeight: 1.25, wordBreak: "break-word" }}>
                {value}{unit && <span style={{ fontSize: 13, color: "#8a99b0", marginLeft: 2 }}>{unit}</span>}
            </div>
        </div>
    );
}

const TAB_NAMES = ["주행", "조향", "인지", "시스템"];

export default function TelemetryPanel({ frame }: { frame: Frame | null }) {
    const [tab, setTab] = useState(0);
    const [hoverTab, setHoverTab] = useState(-1);
    const e = frame?.ego;
    const gear = e?.gear != null ? (GEAR_LABEL[e.gear] ?? String(e.gear)) : "-";
    const modeIdx = e?.disengageType;
    const mode = modeIdx != null ? (MODE_LABEL[modeIdx] ?? "기타") : "-";
    const modeColor = modeIdx != null ? (MODE_COLOR[modeIdx] ?? "#8a99b0") : "#5b7bb0";
    const plan = frame?.planningTrajectory;
    const horizon = (plan && plan.length >= 2) ? Math.max(0, (plan[plan.length - 1][2] ?? 0) - (plan[0][2] ?? 0)).toFixed(1) : "-";
    const sig = frame?.signalColor;
    const sigColor = sig ? (SIGNAL_COLOR[sig] ?? "#8a99b0") : "#5b7bb0";

    return (
        <div style={{ background: "#0e131b", border: "1px solid #232c3a", borderRadius: 10, padding: 16, color: "#cdd8ea", height: "100%", minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", boxSizing: "border-box" }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#cdd8ea", marginBottom: 6 }}>주행 상태</div>

            {/* 속도 hero */}
            <div style={{ textAlign: "center", padding: "14px 0 18px", background: "linear-gradient(180deg,#121a26,#0e131b)", borderRadius: 10, border: "1px solid #1e2836", marginBottom: 12 }}>
                <div style={{ fontSize: 68, fontWeight: 800, color: "#00e5ff", lineHeight: 1, letterSpacing: -1, textShadow: "0 0 18px rgba(0,229,255,0.25)" }}>
                    {e ? (e.speed * 3.6).toFixed(1) : "-"}
                </div>
                <div style={{ fontSize: 16, color: "#8a99b0", letterSpacing: 2, marginTop: 6 }}>
                    km/h{frame?.speedLimit != null && <span style={{ color: "#7d8ca3" }}> · 제한 {(frame.speedLimit * 3.6).toFixed(0)}</span>}
                </div>
            </div>

            {/* 기어 · 모드 */}
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <Badge label="기어" value={gear} color="#5b7bb0" />
                <Badge label="주행 모드" value={mode} color={modeColor} />
            </div>

            {/* 탭 바 (선택형 세그먼트) */}
            <div style={{ fontSize: 11, color: "#7d8ca3", marginBottom: 5 }}>▾ 항목 선택</div>
            <div style={{ display: "flex", gap: 5, background: "#0b1017", padding: 5, borderRadius: 10, border: "1px solid #232c3a", marginBottom: 12 }}>
                {TAB_NAMES.map((t, i) => {
                    const activeTab = tab === i, hov = hoverTab === i && !activeTab;
                    return (
                        <button key={t} onClick={() => setTab(i)}
                            onMouseEnter={() => setHoverTab(i)} onMouseLeave={() => setHoverTab(-1)}
                            style={{
                                flex: 1, minWidth: 0, padding: "9px 0", fontSize: 15.5, fontWeight: 600, cursor: "pointer",
                                borderRadius: 7, transition: "all 0.12s", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                border: `1px solid ${activeTab ? "#3b8bff" : "#2a3546"}`,
                                background: activeTab ? "#1f6feb" : hov ? "#232f40" : "#171f2b",
                                color: activeTab ? "#fff" : hov ? "#dbe4f2" : "#9aa9c0",
                                boxShadow: activeTab ? "0 2px 8px rgba(31,111,235,0.4)" : "none",
                            }}>{t}</button>
                    );
                })}
            </div>

            {/* 스크롤 영역: 탭 내용만 스크롤(헤더 고정) */}
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", marginRight: -8, paddingRight: 8 }}>
            {/* 탭 1: 주행 */}
            {tab === 0 && (
                <div>
                    <Bar label="스로틀" pct={e?.throttle ?? 0} valueText={e ? `${e.throttle.toFixed(0)}%` : "-"} color="#2ecc71" />
                    <Bar label="브레이크" pct={e?.brake ?? 0} valueText={e ? `${e.brake.toFixed(0)}%` : "-"} color="#e74c3c" />
                    <Bar label="배터리" pct={e?.batteryPercentage ?? 0} valueText={e?.batteryPercentage != null ? `${e.batteryPercentage}%` : "-"} color="#3aa0ff" />
                    <div style={{ ...grid3, marginTop: 4 }}>
                        <Chip label="가속도" value={num(e?.accel, 2)} unit="m/s²" />
                        <Chip label="저크" value={num(e?.jerk, 2)} />
                        <Chip label="장애물" value={frame?.obstacles?.length ?? 0} />
                    </div>
                </div>
            )}

            {/* 탭 2: 조향·경로 */}
            {tab === 1 && (
                <div style={grid3}>
                    <Chip label="조향각" value={num(e?.steeringAngle)} unit="°" />
                    <Chip label="조향률" value={num(e?.steering, 0)} unit="%" />
                    <Chip label="조향비" value={num(e?.steeringRatio, 1)} />
                    <Chip label="곡률 κ" value={num(e?.kappa, 3)} />
                    <Chip label="곡률변화" value={num(e?.dkappa, 3)} />
                    <Chip label="방위" value={rad2deg(e?.heading)} unit="°" />
                    <Chip label="속도방위" value={rad2deg(e?.speedHeading)} unit="°" />
                    <Chip label="계획 지평" value={horizon} unit="s" />
                    <Chip label="계획점" value={frame?.planningTrajectory?.length ?? 0} />
                </div>
            )}

            {/* 탭 3: 인지·판단 */}
            {tab === 2 && (
                <div>
                    <div style={grid2}>
                        <div style={{ background: "#141b26", border: `1px solid ${sigColor}66`, borderRadius: 8, padding: "11px 8px", textAlign: "center" }}>
                            <div style={{ fontSize: 14, color: "#8a99b0", marginBottom: 4 }}>신호등</div>
                            <div style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                                <span style={{ width: 14, height: 14, borderRadius: "50%", background: sigColor, boxShadow: `0 0 10px ${sigColor}` }} />
                                <span style={{ fontSize: 20, fontWeight: 700, color: sigColor }}>{sig || "-"}</span>
                            </div>
                        </div>
                        <Chip label="속도 제한" value={frame?.speedLimit != null ? (frame.speedLimit * 3.6).toFixed(0) : "-"} unit="km/h" />
                    </div>
                    <div style={{ ...grid2, marginTop: 6 }}>
                        <Chip label="주요 결정" value={frame?.decisionType != null ? (DECISION_LABEL[frame.decisionType] ?? "-") : "-"} />
                        <Chip label="정지 사유" value={frame?.stopReason != null ? (STOP_REASON_LABEL[frame.stopReason] ?? "-") : "-"} />
                    </div>
                    <div style={{ marginTop: 6 }}>
                        <Chip label="양보 중" value={e?.yielded == null ? "-" : (e.yielded ? "예" : "아니오")} color={e?.yielded ? "#f39c12" : undefined} />
                    </div>
                </div>
            )}

            {/* 탭 4: 시스템 */}
            {tab === 3 && (
                <div>
                    <div style={sectionTitle}>제어 추종 오차</div>
                    <div style={grid3}>
                        <Chip label="거리오차" value={num(frame?.ctrlStationError, 2)} unit="m" />
                        <Chip label="횡오차" value={num(frame?.ctrlLateralError, 2)} unit="m" />
                        <Chip label="방향오차" value={num(frame?.ctrlHeadingError, 3)} unit="rad" />
                    </div>
                    <div style={sectionTitle}>지연 (ms)</div>
                    <div style={grid3}>
                        <Chip label="전체" value={num(frame?.latencyMs, 0)} />
                        <Chip label="계획" value={num(frame?.delays?.planning, 0)} />
                        <Chip label="예측" value={num(frame?.delays?.prediction, 0)} />
                        <Chip label="인지" value={num(frame?.delays?.perception, 0)} />
                        <Chip label="제어" value={num(frame?.delays?.control, 0)} />
                        <Chip label="측위" value={num(frame?.delays?.localization, 0)} />
                    </div>
                    <div style={sectionTitle}>안전</div>
                    <div style={grid2}>
                        <Chip label="RSS" value={frame?.isRssSafe == null ? "-" : (frame.isRssSafe ? "안전" : "위험")} color={frame?.isRssSafe === false ? "#e74c3c" : "#2ecc71"} />
                        <Chip label="사이렌" value={frame?.isSirenOn == null ? "-" : (frame.isSirenOn ? "ON" : "OFF")} color={frame?.isSirenOn ? "#f39c12" : undefined} />
                    </div>
                    {frame?.engageAdvice && (
                        <div style={{ marginTop: 12 }}><Tag color="cyan" style={{ fontSize: 14, padding: "3px 9px" }}>인게이지 조언: {frame.engageAdvice}</Tag></div>
                    )}
                </div>
            )}
            </div>
        </div>
    );
}
